// A match on the canvas (teams and matches, Ben 2026-10-30 — docs/modules/teams-and-matches.md
// "Match"). Dragged out of Match Results (its score) or a team's Matches (its bar); a Match module
// it lands on shows it. The module keeps it as `_match` (its `_`-state, so Undo and
// saved dashboards carry it). It holds what the card heads with, so only the extras (xG, goals,
// reds) are fetched.

import type { MatchesResponse, PlayerCurrentLeague, SeasonsResponse } from "@catbento/shared";
import type { TeamRef } from "./team.js";
import type { LiveApi } from "./api.js";
import type { PlayerId } from "../data/players.js";

export interface MatchRef {
  id: number;
  competitionId: number;
  /** The season code `/matches` and `/team-match-stats` take. */
  season: string;
  /** "YYYY-MM-DD". */
  date: string;
  kickOff: string | null;
  home: { id: number; name: string };
  away: { id: number; name: string };
  homeScore: number;
  awayScore: number;
}

function isSide(v: unknown): v is { id: number; name: string } {
  const s = v as { id?: unknown; name?: unknown } | null;
  return !!s && typeof s.id === "number" && typeof s.name === "string";
}

/** The match a module shows (`_match`), or null. */
export function matchOfModule(mod: Element | null): MatchRef | null {
  if (!mod) return null;
  const v = (mod as unknown as { _match?: unknown })._match;
  if (v === undefined || v === null) return null;
  const m = v as Partial<MatchRef>;
  if (
    typeof m.id !== "number" ||
    typeof m.competitionId !== "number" ||
    typeof m.season !== "string" ||
    typeof m.date !== "string" ||
    !isSide(m.home) ||
    !isSide(m.away) ||
    typeof m.homeScore !== "number" ||
    typeof m.awayScore !== "number"
  ) {
    throw new Error(`A module's _match isn't a match: ${JSON.stringify(v)}`);
  }
  return {
    id: m.id,
    competitionId: m.competitionId,
    season: m.season,
    date: m.date.slice(0, 10),
    kickOff: typeof m.kickOff === "string" ? m.kickOff : null,
    home: m.home,
    away: m.away,
    homeScore: m.homeScore,
    awayScore: m.awayScore,
  };
}

/** A match from a played result, or null when the result lacks what a card needs (team ids or a
 *  season: the snapshot site's results). */
export function matchOfResult(
  r: {
    matchId: number;
    matchDate: string;
    kickOff?: string | null;
    homeTeamId?: number;
    awayTeamId?: number;
    homeTeamName: string;
    awayTeamName: string;
    homeScore: number | null;
    awayScore: number | null;
  },
  competitionId: number,
  season: string | undefined,
): MatchRef | null {
  if (r.homeTeamId === undefined || r.awayTeamId === undefined || season === undefined) return null;
  if (r.homeScore === null || r.awayScore === null) return null;
  return {
    id: r.matchId,
    competitionId,
    season,
    date: r.matchDate.slice(0, 10),
    kickOff: r.kickOff ?? null,
    home: { id: r.homeTeamId, name: r.homeTeamName },
    away: { id: r.awayTeamId, name: r.awayTeamName },
    homeScore: r.homeScore,
    awayScore: r.awayScore,
  };
}

/** The team's most recent played game in a season's matches (newest date; the higher match id on
 *  the same day), or null. Pure. */
export function latestPlayedMatch<M extends { matchId: number; matchDate: string; homeTeamId?: number; awayTeamId?: number; homeScore: number | null; awayScore: number | null }>(
  matches: readonly M[],
  teamId: number,
): M | null {
  let best: M | null = null;
  for (const m of matches) {
    if (m.homeTeamId !== teamId && m.awayTeamId !== teamId) continue;
    if (m.homeScore === null || m.awayScore === null) continue;
    if (!best || m.matchDate > best.matchDate || (m.matchDate === best.matchDate && m.matchId > best.matchId)) best = m;
  }
  return best;
}

/** The match a fresh Match module opens on (Ben, 2026-10-30: "the last match the current live
 *  player played in … Arsenal's most recent fixture"): the player's current club's most recent
 *  played game in its league this season. Null when the player has no domestic league on record
 *  or the club hasn't played yet. */
export async function loadLatestMatch(api: LiveApi, playerId: PlayerId): Promise<MatchRef | null> {
  const club = await api.get<PlayerCurrentLeague>(`/player-current-league?playerId=${playerId}`);
  if (club.competitionId === null || club.seasonId === null || club.teamId === null) return null;
  const { matches } = await api.get<MatchesResponse>(`/matches?competitionId=${club.competitionId}&seasonId=${club.seasonId}`);
  const latest = latestPlayedMatch(matches, club.teamId);
  return latest ? matchOfResult(latest, club.competitionId, String(club.seasonId)) : null;
}

/** A team's latest played game: in the season it was picked up in, else its league's newest season
 *  with one (`/seasons` lists them newest first). Null when it has none. */
export async function loadLatestTeamMatch(api: LiveApi, team: TeamRef): Promise<MatchRef | null> {
  const seasons = team.season !== null ? [team.season] : (await api.get<SeasonsResponse>(`/seasons?competitionId=${team.competitionId}`)).seasons.map((s) => String(s.id));
  for (const season of seasons) {
    const { matches } = await api.get<MatchesResponse>(`/matches?competitionId=${team.competitionId}&seasonId=${season}`);
    const latest = latestPlayedMatch(matches, team.id);
    if (latest) return matchOfResult(latest, team.competitionId, season);
  }
  return null;
}

/** Whose latest game a Match module should find (`_matchFor`): a player or a team dropped on it. */
export type MatchFor = { player: PlayerId } | { team: TeamRef };

/** A module's `_matchFor`, or null. */
export function matchForOfModule(mod: Element | null): MatchFor | null {
  const v = mod ? (mod as unknown as { _matchFor?: unknown })._matchFor : undefined;
  if (v === undefined || v === null) return null;
  const f = v as { player?: unknown; team?: unknown };
  if (typeof f.player === "number") return { player: f.player as PlayerId };
  if (f.team && typeof f.team === "object") return { team: f.team as TeamRef };
  throw new Error(`A module's _matchFor isn't a player or a team: ${JSON.stringify(v)}`);
}
